/**
 * AudioMix — Karaoke audio mixing panel.
 *
 * Handles:
 *  • File upload or URL load of a backing track
 *  • Agora BufferSourceAudioTrack publishing (talent's audience hears the music)
 *  • Seek / volume / loop controls
 *
 * Design system: Sing4You dark-navy + orange accent (#f97316 / #fbbf24)
 * Must be rendered inside <AgoraRTCProvider> (uses useRTCClient).
 */

import { useEffect, useRef, useState } from "react";
import AgoraRTC, { useRTCClient } from "agora-rtc-react";
import type { IBufferSourceAudioTrack } from "agora-rtc-react";
import { uploadFile } from "../../../../services/api/uploadFile";
import {
  Box,
  IconButton,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AudioFile,
  Close,
  CloudUpload,
  GraphicEq,
  Loop,
  MusicNote,
  Pause,
  PlayArrow,
  SkipNext,
  SkipPrevious,
  VolumeDown,
  VolumeUp,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import "../../../../styles/sing4you-live.css";

/* ── Types ────────────────────────────────────────────────────── */
interface AudioMixProps {
  open: boolean;
  onClose: () => void;
}

type PlayState = "idle" | "loading" | "ready" | "playing" | "paused";

/* ── Constants ────────────────────────────────────────────────── */
const ORANGE       = "#f97316";
const ORANGE_HOVER = "#ea6c0a";
const ORANGE_DIM   = "rgba(249,115,22,0.15)";
const GOLD         = "#fbbf24";

const VALID_AUDIO_TYPES = [
  "audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4",
  "audio/aac", "audio/webm", "audio/flac", "audio/x-m4a",
];
const VALID_AUDIO_EXT = /\.(mp3|wav|ogg|m4a|aac|webm|flac)$/i;

function isValidAudioFile(f: File): boolean {
  return VALID_AUDIO_TYPES.includes(f.type) || VALID_AUDIO_EXT.test(f.name);
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/* ── Shared MUI Slider sx (orange gradient) ──────────────────── */
const sliderSx = {
  color: ORANGE,
  height: 4,
  "& .MuiSlider-thumb": {
    width: 13,
    height: 13,
    backgroundColor: GOLD,
    boxShadow: `0 0 8px rgba(251,191,36,0.7) !important`,
    "&:hover": { boxShadow: `0 0 0 8px ${ORANGE_DIM}` },
  },
  "& .MuiSlider-track": {
    background: `linear-gradient(90deg, ${ORANGE}, #ef4444)`,
    border: "none",
  },
  "& .MuiSlider-rail": { backgroundColor: "rgba(255,255,255,0.12)" },
};

/* ═══════════════════════════════════════════════════════════════ */
export default function AudioMix({ open, onClose }: AudioMixProps) {
  const client = useRTCClient();

  const [url,            setUrl]            = useState("");
  const [playState,      setPlayState]      = useState<PlayState>("idle");
  const [isLooping,      setIsLooping]      = useState(true);
  const [volume,         setVolume]         = useState(50);
  const [currentTime,    setCurrentTime]    = useState(0);
  const [duration,       setDuration]       = useState(0);
  const [songLabel,      setSongLabel]      = useState("");
  const [selectedFile,   setSelectedFile]   = useState<File | null>(null);
  const [isUploading,    setIsUploading]    = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const trackRef        = useRef<IBufferSourceAudioTrack | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadTickRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPublishedRef  = useRef(false);

  /* ── Cleanup ────────────────────────────────────────────────── */
  const cleanup = async (resetState = true) => {
    stopProgressTimer();
    if (trackRef.current) {
      try {
        trackRef.current.stopProcessAudioBuffer();
        if (isPublishedRef.current && client.connectionState === "CONNECTED") {
          await client.unpublish([trackRef.current as any]);
        }
        isPublishedRef.current = false;
        trackRef.current.close();
      } catch (err) {
        console.warn("[AudioMix] Cleanup error:", err);
        isPublishedRef.current = false;
      }
      trackRef.current = null;
    }
    if (resetState) {
      setPlayState("idle");
      setCurrentTime(0);
      setDuration(0);
    }
  };

  useEffect(() => {
    if (!open) {
      cleanup(true);
      setSelectedFile(null);
      setIsUploading(false);
      setUploadProgress(0);
    }
    return () => { cleanup(true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── Progress timer ─────────────────────────────────────────── */
  const startProgressTimer = () => {
    stopProgressTimer();
    progressTimerRef.current = setInterval(() => {
      if (trackRef.current) {
        setCurrentTime(trackRef.current.getCurrentTime());
        setDuration(trackRef.current.duration);
      }
    }, 500);
  };
  const stopProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  /* ── Load source ────────────────────────────────────────────── */
  const loadSource = async (source: string | File, label: string) => {
    setPlayState("loading");
    setCurrentTime(0);
    setDuration(0);
    try {
      await cleanup(false);
      const track = await AgoraRTC.createBufferSourceAudioTrack({ source } as any);
      track.setVolume(volume);
      (track as any).on("source-state-change", (state: string) => {
        if (state === "stopped") {
          stopProgressTimer();
          isPublishedRef.current = false;
          setPlayState("ready");
          setCurrentTime(0);
        }
      });
      trackRef.current = track;
      setDuration(track.duration);
      setSongLabel(label);
      setPlayState("ready");
      enqueueSnackbar("Audio loaded — press Play to start mixing", { variant: "success" });
    } catch (err: any) {
      console.error("[AudioMix] Load failed:", err);
      enqueueSnackbar(err?.message || "Failed to load audio.", { variant: "error" });
      setPlayState("idle");
    }
  };

  /* ── URL load ───────────────────────────────────────────────── */
  const handleLoad = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      enqueueSnackbar("Please enter an audio file URL", { variant: "warning" });
      return;
    }
    try {
      const parts    = trimmedUrl.split("/");
      const filename = decodeURIComponent(parts[parts.length - 1].split("?")[0]);
      await loadSource(trimmedUrl, filename || "Background Music");
    } catch {
      await loadSource(trimmedUrl, "Background Music");
    }
  };

  /* ── File handlers ──────────────────────────────────────────── */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isValidAudioFile(file)) {
      enqueueSnackbar("Invalid file type. Use MP3, WAV, OGG, M4A, AAC, or FLAC.", { variant: "error" });
      return;
    }
    setSelectedFile(file);
    e.target.value = "";
  };

  const handleUploadAndLoad = async () => {
    if (!selectedFile) return;
    const fileToLoad = selectedFile;
    const label      = selectedFile.name;
    setIsUploading(true);
    setUploadProgress(0);
    uploadTickRef.current = setInterval(
      () => setUploadProgress((p) => (p < 85 ? p + 8 : p)),
      350
    );
    try {
      const uploadedUrl = await uploadFile(selectedFile);
      clearInterval(uploadTickRef.current!);
      setUploadProgress(100);
      setSelectedFile(null);
      setIsUploading(false);
      setUploadProgress(0);
      setUrl(uploadedUrl);
      await loadSource(fileToLoad, label);
    } catch (err: any) {
      clearInterval(uploadTickRef.current!);
      setIsUploading(false);
      setUploadProgress(0);
      enqueueSnackbar(err?.message || "Upload failed.", { variant: "error" });
    }
  };

  /* ── Playback controls ──────────────────────────────────────── */
  const handlePlay = async () => {
    if (!trackRef.current) return;
    if (playState === "paused") {
      trackRef.current.resumeProcessAudioBuffer();
      setPlayState("playing");
      startProgressTimer();
      return;
    }
    if (client.connectionState !== "CONNECTED") {
      enqueueSnackbar("Stream not connected yet — please wait.", { variant: "warning" });
      return;
    }
    try {
      trackRef.current.startProcessAudioBuffer({ loop: isLooping });
      trackRef.current.play();
      await client.publish([trackRef.current as any]);
      isPublishedRef.current = true;
      setPlayState("playing");
      startProgressTimer();
    } catch (err: any) {
      console.error("[AudioMix] Publish failed:", err);
      enqueueSnackbar("Failed to start audio mixing", { variant: "error" });
    }
  };

  const handlePause = () => {
    if (!trackRef.current || playState !== "playing") return;
    trackRef.current.pauseProcessAudioBuffer();
    stopProgressTimer();
    setPlayState("paused");
  };

  const handleRestart = async () => {
    if (!trackRef.current) return;
    stopProgressTimer();
    trackRef.current.stopProcessAudioBuffer();
    if (isPublishedRef.current) {
      try {
        if (client.connectionState === "CONNECTED") {
          await client.unpublish([trackRef.current as any]);
        }
      } catch {}
      isPublishedRef.current = false;
    }
    setPlayState("ready");
    setCurrentTime(0);
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const v = value as number;
    setVolume(v);
    if (trackRef.current) trackRef.current.setVolume(v);
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    const seconds = value as number;
    if (trackRef.current) {
      trackRef.current.seekAudioBuffer(seconds);
      setCurrentTime(seconds);
    }
  };

  if (!open) return null;

  const isBusy      = playState === "loading" || isUploading;
  const isLoading   = playState === "loading";
  const isReady     = playState === "ready";
  const isPlaying   = playState === "playing";
  const isPaused    = playState === "paused";
  const hasTrack    = isReady || isPlaying || isPaused;
  const isConnected = client.connectionState === "CONNECTED";
  const progress    = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  /* ── Status dot colour ──────────────────────────────────────── */
  const statusColor = isPlaying ? "#22C55E"
    : isPaused                  ? "#F59E0B"
    : isReady                   ? "#60A5FA"
    : isBusy                    ? ORANGE
    : "rgba(255,255,255,0.25)";

  const statusText = isPlaying              ? "Mixing — audience can hear background music"
    : isPaused                              ? "Paused"
    : isReady && !isConnected               ? "Waiting for stream to connect…"
    : isReady                               ? "Ready — press Play to start mixing"
    : isUploading                           ? "Uploading audio file…"
    : isLoading                             ? "Loading audio…"
    : "Upload a file or paste a URL to start";

  /* ── Common MUI sx shortcuts ────────────────────────────────── */
  const panelBg   = { bgcolor: "#060914", color: "#fff" };
  const surfaceBg = { bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)" };

  return (
    <Box
      className="audiomix-root"
      sx={{
        backgroundColor: "#0a0d1f",
        backdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 3,
        boxShadow: "0 16px 48px rgba(0,0,0,0.7)",
        p: 2.5,
        minWidth: 320,
      }}
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <GraphicEq sx={{ color: ORANGE, fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            Karaoke Studio
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.55)" }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      {/* ── Upload drop zone ────────────────────────────────── */}
      {!isUploading && !selectedFile && (
        <>
          <input
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            style={{ display: "none" }}
            id="audiomix-file-input"
            type="file"
            onChange={handleFileChange}
            disabled={isBusy}
          />
          <label htmlFor="audiomix-file-input">
            <Box
              component="span"
              sx={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                py: 1.1, borderRadius: 2,
                border: "1px dashed rgba(255,255,255,0.18)",
                cursor: isBusy ? "not-allowed" : "pointer",
                backgroundColor: "rgba(255,255,255,0.03)",
                color: "rgba(255,255,255,0.55)", fontSize: 13, fontWeight: 500,
                transition: "all 0.2s", mb: 1.5,
                "&:hover": {
                  borderColor: ORANGE, color: "#fff",
                  backgroundColor: ORANGE_DIM,
                },
              }}
            >
              <CloudUpload sx={{ fontSize: 18, color: ORANGE }} />
              Choose MP3 / Audio File
            </Box>
          </label>

          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.08)" }} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.28)", fontSize: 11 }}>
              or paste URL
            </Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.08)" }} />
          </Stack>
        </>
      )}

      {/* ── File selected ───────────────────────────────────── */}
      {selectedFile && !isUploading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 1.5, borderRadius: 2, ...surfaceBg }}>
          <AudioFile sx={{ color: ORANGE, fontSize: 26, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.38)", fontSize: 11 }}>
              {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
            </Typography>
          </Box>
          <Stack direction="row" gap={0.5}>
            <Box
              component="button"
              onClick={handleUploadAndLoad}
              sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1.5, py: 0.75, borderRadius: 1.5, border: "none",
                backgroundColor: ORANGE, color: "#fff",
                fontWeight: 600, fontSize: 12, cursor: "pointer",
                "&:hover": { backgroundColor: ORANGE_HOVER },
              }}
            >
              <CloudUpload sx={{ fontSize: 14 }} /> Upload
            </Box>
            <IconButton size="small" onClick={() => setSelectedFile(null)} sx={{ color: "rgba(255,255,255,0.38)", "&:hover": { color: "#fff" } }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* ── Upload progress ─────────────────────────────────── */}
      {isUploading && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, ...surfaceBg }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <CloudUpload sx={{ color: ORANGE, fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12, flex: 1 }} noWrap>
              Uploading {selectedFile?.name ?? "file"}…
            </Typography>
            <Typography variant="caption" sx={{ color: ORANGE, fontWeight: 700, fontSize: 12 }}>
              {uploadProgress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              borderRadius: 2, height: 5,
              backgroundColor: "rgba(255,255,255,0.07)",
              "& .MuiLinearProgress-bar": { borderRadius: 2, backgroundColor: ORANGE },
            }}
          />
        </Box>
      )}

      {/* ── URL input ───────────────────────────────────────── */}
      {!isUploading && !selectedFile && (
        <Stack direction="row" gap={1} mb={2}>
          <TextField
            fullWidth size="small"
            placeholder="https://… (MP3, AAC, WAV)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !isBusy && handleLoad()}
            disabled={isBusy}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff", fontSize: 13,
                backgroundColor: "rgba(255,255,255,0.05)",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.13)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.28)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
              },
              "& input::placeholder": { color: "rgba(255,255,255,0.28)", fontSize: 12 },
            }}
          />
          <Box
            component="button"
            onClick={handleLoad}
            disabled={isBusy}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: 58, px: 1.5, borderRadius: 2, border: "none",
              cursor: isBusy ? "not-allowed" : "pointer",
              backgroundColor: isBusy ? "rgba(255,255,255,0.08)" : ORANGE,
              color: "#fff", fontWeight: 600, fontSize: 13,
              "&:hover:not(:disabled)": { backgroundColor: ORANGE_HOVER },
            }}
          >
            {isLoading ? "…" : "Load"}
          </Box>
        </Stack>
      )}

      {/* ── Song label ──────────────────────────────────────── */}
      {hasTrack && songLabel && (
        <Stack direction="row" alignItems="center" gap={0.75} mb={1.5}>
          <MusicNote sx={{ fontSize: 14, color: ORANGE }} />
          <Typography variant="caption" noWrap sx={{ color: "rgba(255,255,255,0.65)", maxWidth: 280, fontSize: 12 }}>
            {songLabel}
          </Typography>
        </Stack>
      )}

      {/* ── Seek bar ────────────────────────────────────────── */}
      {hasTrack && duration > 0 && (
        <Box mb={0.5}>
          <Box sx={{ position: "relative", px: 0 }}>
            <Slider
              value={currentTime}
              min={0}
              max={duration}
              step={1}
              onChange={handleSeek}
              sx={sliderSx}
            />
          </Box>
          <Stack direction="row" justifyContent="space-between" mt={-0.5}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
              {formatTime(duration)}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── Playback controls: [⏮] [large play] [⏭] [loop] ── */}
      <Stack direction="row" alignItems="center" justifyContent="center" gap={1} mt={hasTrack ? 1 : 0} mb={1.5}>
        {/* Restart / previous */}
        <Tooltip title="Restart" arrow>
          <span>
            <IconButton
              onClick={handleRestart}
              disabled={!hasTrack}
              sx={{
                color: hasTrack ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.2)",
                width: 36, height: 36,
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <SkipPrevious />
            </IconButton>
          </span>
        </Tooltip>

        {/* Main play / pause */}
        <Tooltip
          title={
            !isConnected      ? "Waiting for stream connection…"
            : isPlaying       ? "Pause"
            : isPaused        ? "Resume"
            : "Play"
          }
          arrow
        >
          <span>
            <IconButton
              onClick={isPlaying ? handlePause : handlePlay}
              disabled={!hasTrack || !isConnected}
              sx={{
                backgroundColor: ORANGE, color: "#fff", width: 48, height: 48,
                boxShadow: hasTrack ? `0 0 18px rgba(249,115,22,0.5)` : "none",
                "&:hover": { backgroundColor: ORANGE_HOVER, boxShadow: `0 0 26px rgba(249,115,22,0.65)` },
                "&:disabled": { backgroundColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)", boxShadow: "none" },
              }}
            >
              {isPlaying ? <Pause sx={{ fontSize: 22 }} /> : <PlayArrow sx={{ fontSize: 22 }} />}
            </IconButton>
          </span>
        </Tooltip>

        {/* Skip next (placeholder — wire to queue when ready) */}
        <Tooltip title="Next (queue)" arrow>
          <span>
            <IconButton
              disabled={!hasTrack}
              sx={{
                color: hasTrack ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.2)",
                width: 36, height: 36,
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <SkipNext />
            </IconButton>
          </span>
        </Tooltip>

        {/* Loop toggle */}
        <Tooltip title={isLooping ? "Loop: ON" : "Loop: OFF"} arrow>
          <IconButton
            onClick={() => setIsLooping((p) => !p)}
            sx={{
              backgroundColor: isLooping ? ORANGE_DIM : "rgba(255,255,255,0.07)",
              color: isLooping ? ORANGE : "rgba(255,255,255,0.45)",
              width: 36, height: 36,
              border: isLooping ? `1px solid rgba(249,115,22,0.4)` : "1px solid rgba(255,255,255,0.09)",
              "&:hover": { backgroundColor: isLooping ? "rgba(249,115,22,0.25)" : "rgba(255,255,255,0.12)" },
            }}
          >
            <Loop sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Volume ──────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" gap={1.5}>
        <VolumeDown sx={{ color: "rgba(255,255,255,0.45)", fontSize: 18, flexShrink: 0 }} />
        <Slider
          value={volume}
          min={0}
          max={100}
          onChange={handleVolumeChange}
          sx={sliderSx}
        />
        <VolumeUp sx={{ color: "rgba(255,255,255,0.45)", fontSize: 18, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", minWidth: 28, textAlign: "right", fontSize: 11 }}>
          {volume}%
        </Typography>
      </Stack>

      {/* ── Status row ──────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" gap={0.75} mt={1.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, backgroundColor: statusColor }} />
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
          {statusText}
        </Typography>
      </Stack>
    </Box>
  );
}
